import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/TermsAndConditions.css';

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "1. Introducción",
      content: "Bienvenido a Runamatic. Estos Términos y Condiciones regulan el acceso y uso de nuestra plataforma y servicios. Al utilizar nuestros servicios, aceptas cumplir con estos términos. Si no estás de acuerdo con alguna parte, por favor no utilices nuestros servicios."
    },
    {
      title: "2. Definiciones", 
      content: [
        "\"Cliente\": Se refiere a cualquier individuo o entidad que se suscribe a nuestros planes de servicio.",
        "\"Servicio\": Incluye los servicios ofrecidos por Runamatic, así como el acceso y uso de nuestra plataforma de automatización."
      ]
    },
    {
      title: "3. Planes de Servicio",
      content: "Runamatic ofrece los siguientes planes:",
      items: [
        "Pro: Acceso completo a todas las herramientas de la plataforma con Inteligencia Artificial incluida (OpenAI GPT-4o) y todos los canales. Modalidad auto gestión, el cliente configura sus propias automatizaciones.",
        "Full: Incluye todo el Plan Pro más asesoría completa de estrategia comercial, análisis y mejora de procesos, y la configuración 100% personalizada por nuestro equipo. Para integrar sistemas externos se debe presupuestar la integración.",
        "Plan de Mantenimiento: Opción disponible al concluir la configuración del Plan Full, para mantener el bot operativo con soporte y actualizaciones puntuales."
      ]
    },
    {
      title: "4. Condiciones de Pago y Acceso a la Plataforma",
      subsections: [
        {
          subtitle: "4.1 Pago y Acceso a la Cuenta",
          content: "Los clientes deben pagar el monto correspondiente al plan elegido. Al suscribirse, recibirán acceso a su cuenta dentro de un plazo de 24 horas, momento en el cual se proporcionarán las instrucciones para iniciar el uso de la plataforma. Las suscripciones son mensuales."
        },
        {
          subtitle: "4.2 Configuración y Soporte",
          content: "Los clientes son responsables de configurar y vincular sus cuentas de redes sociales, web y mensajería (Instagram, Facebook, WhatsApp, etc.) en el Plan Pro. En el Plan Full, nuestro equipo proporcionará soporte personalizado y configuración completa del sistema.",
          important: "Importante: La configuración del portfolio comercial de Meta, las cuentas de Instagram, Facebook, WhatsApp y Telegram son exclusivamente responsabilidad de nuestros clientes. Esto aplica a todos los casos, para todos los planes y servicios."
        },
        {
          subtitle: "4.3 Condiciones de Elegibilidad para Reembolso",
          content: "Los costos asociados a la apertura de cuentas y la activación de tecnología de inteligencia artificial implican que no se emitirán devoluciones por servicios ya activados. Esto asegura la sostenibilidad del servicio para todos nuestros usuarios."
        }
      ]
    },
    {
      title: "5. Satisfacción del Cliente",
      subsections: [
        {
          subtitle: "5.1 Garantía de Satisfacción",
          content: "Nuestra prioridad es la satisfacción del cliente. Si en algún momento consideras que nuestra plataforma no cumple con tus expectativas, puedes suspender el servicio sin costo adicional."
        },
        {
          subtitle: "5.2 Cancelación Sin Complicaciones",
          content: "Los clientes pueden cancelar el servicio en cualquier momento de manera rápida y sin procesos complicados. La cancelación será efectiva de inmediato y no se aplicarán cargos adicionales."
        },
        {
          subtitle: "5.3 Excepción de Reembolso",
          content: "El cliente acepta que, al contratar nuestros servicios, Runamatic incurre en costos inmediatos, por lo que no se efectuarán devoluciones de los montos pagados una vez que el servicio esté activado."
        }
      ]
    },
    {
      title: "6. Proceso de Configuración del Plan Full",
      content: "Al suscribirte al Plan Full, se seguirá el siguiente proceso para configurar tu cuenta:",
      items: [
        "Grupo de WhatsApp dedicado y reunión inicial para conocer tu marca.",
        "Análisis de tu estrategia comercial actual.",
        "Propuesta de soluciones tecnológicas adaptadas a tu negocio.",
        "Toma de información detallada del negocio, productos y servicios.",
        "Configuración inicial por nuestro equipo de expertos.",
        "Entrega de Demo para que el cliente pruebe el sistema antes de conectar canales reales.",
        "Conexión a los canales reales (WhatsApp, Instagram, Telegram, TikTok, etc.).",
        "Refinamiento continuo: nuestro equipo queda disponible para refinar, actualizar y agregar funcionalidades."
      ],
      subsections: [
        {
          subtitle: "6.1 Tiempos de entrega",
          content: "El tiempo de entrega de este servicio dependerá de la complejidad de la automatización, las automatizaciones simples demorarán menos que las complejas. En ningún momento nos comprometemos a definir un período fijo de entrega ya que son muchos los factores que pueden influir en esto."
        },
        {
          subtitle: "6.2 Asignación de Programadores y Ausencias",
          content: "En caso de que el programador asignado a la cuenta del cliente tenga un time off por enfermedad, vacaciones, o cualquier otra razón superior a 72 horas, Runamatic se compromete a encontrar un reemplazo o a intervenir para no demorar los procesos."
        },
        {
          subtitle: "6.3 Compromiso del Cliente",
          content: "El cliente se compromete a proporcionar la información y el feedback necesarios de manera oportuna para facilitar la continuidad y eficiencia del proceso de configuración. Esto incluye la disponibilidad para reuniones y la rápida respuesta a las solicitudes del equipo."
        },
        {
          subtitle: "6.4 Plan de Mantenimiento",
          content: "Al finalizar la configuración del Plan Full, el cliente podrá continuar con refinamientos o migrar a un Plan de Mantenimiento para mantener el bot operativo con soporte y actualizaciones a pedido."
        }
      ]
    },
    {
      title: "7. Alcance de los Servicios y Limitaciones",
      subsections: [
        {
          subtitle: "7.1 Naturaleza de los Servicios",
          content: "Los planes de Runamatic están diseñados para ofrecer un excelente valor y automatización. Sin embargo, no se debe esperar la atención continua de un profesional trabajando 8 horas al día. La naturaleza de los servicios se basa en la automatización y no en un manejo constante de la cuenta. Los horarios establecidos para la comunicación son de Lunes a Viernes, de 9:00 a 18:00 horas GTM-3."
        },
        {
          subtitle: "7.2 Participación Activa del Cliente",
          content: "Para maximizar la efectividad del bot, se recomienda que el cliente esté disponible para dar feedback y proporcionar información durante la implementación y ajustes."
        },
        {
          subtitle: "7.3 Valor del Servicio",
          content: "Los planes están diseñados para ser accesibles y pueden no ofrecer una atención personalizada intensiva. Si el cliente requiere mayor acompañamiento, este servicio específico no está dentro del alcance de nuestras ofertas."
        }
      ]
    },
    {
      title: "8. Cambios en la Política",
      content: "Runamatic se reserva el derecho de modificar o actualizar estos Términos y Condiciones en cualquier momento. Se recomienda a los clientes revisar periódicamente esta página para estar al tanto de los cambios. Cualquier modificación entrará en vigor una vez publicada en nuestro sitio web."
    },
    {
      title: "9. Limitación de Responsabilidad",
      content: "Runamatic no será responsable por daños directos, indirectos, incidentales, o consecuenciales que puedan resultar del uso o la incapacidad de uso de nuestros servicios. El cliente asume toda la responsabilidad por el uso de la plataforma."
    },
    {
      title: "10. Gestión de Suscripciones",
      content: "El cliente es el único responsable de la administración y gestión de sus suscripciones activas a Runamatic a través de la pasarela de pagos seleccionada (como Stripe, PayPal, Mercado Pago u otra habilitada en la plataforma). Esto incluye, entre otros, la actualización de métodos de pago, la revisión de fechas de cobro, la cancelación o modificación de suscripciones y cualquier otra acción relacionada con su cuenta de facturación. Runamatic no se responsabiliza por cargos recurrentes no deseados si el cliente no gestiona adecuadamente su suscripción en la pasarela correspondiente."
    },
    {
      title: "11. Contacto",
      content: "Para consultas relacionadas con estos Términos y Condiciones, puedes contactar a nuestro equipo a través de hola@runamatic.io."
    }
  ];

  return (
    <div className="terms-page">
      {/* Header */}
      <div className="terms-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="back-link">
              <i className="fas fa-arrow-left"></i>
              Volver al inicio
            </Link>
            <h1>Términos y Condiciones</h1>
            <p>Última actualización: {new Date().toLocaleDateString('es-ES', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="terms-content">
        <div className="container">
          <div className="terms-wrapper">
            <motion.div
              className="terms-main"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {sections.map((section, index) => (
                <motion.section
                  key={index}
                  id={`section-${index}`}
                  className="terms-section"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <h2>{section.title}</h2>
                  
                  {typeof section.content === 'string' && (
                    <p>{section.content}</p>
                  )}
                  
                  {Array.isArray(section.content) && (
                    <div className="definitions">
                      {section.content.map((definition, idx) => (
                        <p key={idx}>{definition}</p>
                      ))}
                    </div>
                  )}
                  
                  {section.items && (
                    <ol className="terms-list">
                      {section.items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ol>
                  )}

                  {section.subsections && (
                    <div className="subsections">
                      {section.subsections.map((subsection, idx) => (
                        <div key={idx} className="subsection">
                          <h3>{subsection.subtitle}</h3>
                          <p>{subsection.content}</p>
                          {subsection.important && (
                            <div className="important-notice">
                              <i className="fas fa-exclamation-triangle"></i>
                              <p>{subsection.important}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.section>
              ))}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              className="terms-sidebar"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="sidebar-content">
                <h3>Navegación rápida</h3>
                <nav className="terms-nav">
                  {sections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="nav-item"
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.querySelector(`#section-${index}`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      {section.title}
                    </a>
                  ))}
                </nav>

                <div className="contact-box">
                  <h4>¿Tienes preguntas?</h4>
                  <p>Contacta a nuestro equipo de soporte</p>
                  <a href="mailto:hola@runamatic.io" className="contact-btn">
                    <i className="fas fa-envelope"></i>
                    hola@runamatic.io
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions; 